import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { selectIsAuthenticated, selectAuthLoading, loadUser } from '../store/slices/authSlice';
import { apiClient } from '../services/api';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/login'
}) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectAuthLoading);
  const location = useLocation();

  useEffect(() => {
    // If we have a token but no user, try to load user profile
    if (requireAuth && apiClient.isAuthenticated() && !isAuthenticated && !isLoading) {
      console.log('AuthGuard: Loading user profile...');
      dispatch(loadUser());
    }
  }, [dispatch, isAuthenticated, isLoading, requireAuth]);

  // Debug logging
  console.log('AuthGuard:', {
    path: location.pathname,
    requireAuth,
    isAuthenticated,
    isLoading,
    hasToken: apiClient.isAuthenticated()
  });

  // Show loading while checking authentication OR if we have a token but haven't loaded user yet
  if (isLoading || (requireAuth && apiClient.isAuthenticated() && !isAuthenticated)) {
    console.log('AuthGuard: Still loading...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If auth is required but user is not authenticated AND no token, redirect to login
  if (requireAuth && !isAuthenticated && !apiClient.isAuthenticated()) {
    console.log('AuthGuard: Redirecting to login - not authenticated');
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If auth is not required and user is authenticated, redirect to dashboard
  if (!requireAuth && isAuthenticated) {
    console.log('AuthGuard: Redirecting to dashboard - already authenticated');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('AuthGuard: Rendering children');
  return <>{children}</>;
};

export default AuthGuard;